import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  TextField,
  CircularProgress,
  Chip,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
  Alert,
  Autocomplete,
  Button,
  Collapse,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useTeamBoardForLead,
  useTeamBoardMutations,
  useTeamBoardsList,
  useSetLeadTaskBoard,
  useLeadTasks,
} from "../../hooks/useTeamBoards";
import {
  teamTasksPaths,
  resolveTeamBaseFromPathname,
  isPopupEquipePath,
} from "../../utils/teamNavigation";
import { openTeamTasksPopupWindow } from "../../utils/openTeamTasksPopupWindow";
import OpenInPopupIconButton from "../common/OpenInPopupIconButton";
import AiGenerateTasksDialog from "./AiGenerateTasksDialog";
import TaskCardModalContainer from "../../pages/TeamTasks/components/TaskCardModalContainer";
import { TeamTasksFeedbackProvider } from "../../pages/TeamTasks/components/TeamTasksFeedbackProvider";
import { useTeamBoardAction } from "../../pages/TeamTasks/hooks/useTeamBoardAction";
import { useEffectivePermissions, scopeSetFromPermissions } from "../../hooks/useEffectivePermissions";
import { useAuthContext } from "../../context/AuthContext";
import {
  canManageTeamBoard,
  canCreateTeamTask,
  hasGlobalTeamTasksManage,
} from "../../pages/TeamTasks/utils/teamBoardPermissions";
import { formatTaskDisplayName } from "../../pages/TeamTasks/utils/taskDisplayName";

const SIDEBAR_TASKS_PREVIEW_LIMIT = 25;

function formatDueChip(dueAt) {
  if (!dueAt) return null;
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return null;
  const overdue = d < new Date();
  return {
    label: d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }),
    overdue,
  };
}

const LeadTasksSidebarBoardInner = ({ conversationId, active = false, messages = [] }) => {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { run } = useTeamBoardAction();
  const inCockpit = isPopupEquipePath(location.pathname);
  const tasksPaths = useMemo(
    () => teamTasksPaths(resolveTeamBaseFromPathname(location.pathname)),
    [location.pathname]
  );
  const leadId = conversationId ? Number(conversationId) : null;
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [addingTask, setAddingTask] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [aiTasksOpen, setAiTasksOpen] = useState(false);
  const [linkingBoard, setLinkingBoard] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const { userData } = useAuthContext();
  const { data: permissionsData } = useEffectivePermissions();
  const scopes = scopeSetFromPermissions(permissionsData, userData);

  useEffect(() => {
    if (!active) setMoreOpen(false);
  }, [active]);

  const { data: board, isLoading, isError } = useTeamBoardForLead(leadId, {
    enabled: active && Boolean(leadId),
  });

  const {
    data: leadTasksData,
    isLoading: leadTasksLoading,
    isError: leadTasksError,
    refetch: refetchLeadTasks,
  } = useLeadTasks(leadId, {
    enabled: active && Boolean(leadId),
  });

  const { data: workspaceBoardsPayload } = useTeamBoardsList({
    workspace: 1,
    enabled: active && moreOpen && Boolean(leadId),
  });
  const workspaceBoards = workspaceBoardsPayload?.boards || [];
  const setLeadBoardMutation = useSetLeadTaskBoard(leadId);

  const boardId = board?.id;
  const mutations = useTeamBoardMutations(boardId, {
    extraCacheKeys: leadId ? [["teamBoardForLead", leadId]] : [],
    contextLeadId: leadId,
  });

  const columns = board?.columns || [];
  const canManage = useMemo(() => canManageTeamBoard(scopes, board), [scopes, board]);
  const canCreate = useMemo(() => canCreateTeamTask(scopes, board), [scopes, board]);
  const canChangeLeadBoard = hasGlobalTeamTasksManage(scopes) || canManage;

  const defaultColumn = useMemo(
    () => columns.find((c) => !c.isDone) || columns[0] || null,
    [columns]
  );

  const boardOptions = useMemo(() => {
    const list = (Array.isArray(workspaceBoards) ? workspaceBoards : []).filter(
      (b) => !b.leadId && !b.spaceId && b.currentUserRole
    );
    return [{ id: null, name: t("leadBoardUseDefault"), isDefault: true }, ...list];
  }, [workspaceBoards, t]);

  const selectedBoardOption = useMemo(() => {
    if (board?.boardSource === "lead" && board?.leadBoardId) {
      return boardOptions.find((b) => Number(b.id) === Number(board.leadBoardId)) || boardOptions[0];
    }
    return boardOptions[0];
  }, [board, boardOptions]);

  const taskRows = useMemo(() => {
    const fromApi = Array.isArray(leadTasksData?.tasks) ? leadTasksData.tasks : null;
    if (fromApi) {
      return fromApi.map((task) => ({
        ...task,
        boardName: task.boardName || board?.name || null,
      }));
    }
    const rows = [];
    columns.forEach((col, colIndex) => {
      (col.cards || []).forEach((card) => {
        rows.push({
          ...card,
          boardId: board?.id,
          boardName: board?.name || null,
          columnId: col.id,
          columnName: col.name,
          columnIsDone: Boolean(col.isDone),
          columnIndex: colIndex,
        });
      });
    });
    return rows.sort((a, b) => {
      if (a.columnIsDone !== b.columnIsDone) return a.columnIsDone ? 1 : -1;
      if (a.columnIndex !== b.columnIndex) return a.columnIndex - b.columnIndex;
      return (a.position ?? 0) - (b.position ?? 0);
    });
  }, [leadTasksData, columns, board]);

  const openCount = useMemo(
    () =>
      leadTasksData?.openCount != null
        ? Number(leadTasksData.openCount)
        : taskRows.filter((row) => !row.columnIsDone).length,
    [leadTasksData, taskRows]
  );

  const visibleTasks = taskRows.slice(0, SIDEBAR_TASKS_PREVIEW_LIMIT);
  const hiddenCount = Math.max(0, taskRows.length - visibleTasks.length);

  const boardSourceHint =
    board?.boardSource === "lead"
      ? t("leadBoardSourceOverride")
      : board?.boardSource === "tipo"
        ? t("leadBoardSourceTipo")
        : null;

  const openLinkedBoard = () => {
    if (board?.id) {
      navigate(tasksPaths.board(board.id));
      return;
    }
    if (leadId) navigate(tasksPaths.lead(leadId));
  };

  const openLinkedBoardPopup = () => {
    if (board?.id) {
      openTeamTasksPopupWindow({ boardId: board.id });
      return;
    }
    if (leadId) openTeamTasksPopupWindow({ leadId });
  };

  const handleAddTask = async () => {
    const title = newTaskTitle.trim();
    if (!title || !defaultColumn?.id || addingTask) return;
    setAddingTask(true);
    try {
      const result = await run(
        () =>
          mutations.createCard.mutateAsync({
            columnId: defaultColumn.id,
            title,
            leadId,
            contextLeadId: leadId,
          }),
        { context: "createCard", successKey: "successCardCreated" }
      );
      if (result !== null) {
        setNewTaskTitle("");
        refetchLeadTasks();
      }
    } finally {
      setAddingTask(false);
    }
  };

  const handleBoardLinkChange = async (_e, value) => {
    if (!canChangeLeadBoard || linkingBoard) return;
    const nextId = value?.isDefault || value?.id == null ? null : Number(value.id);
    const currentOverride = board?.boardSource === "lead" ? Number(board.leadBoardId) : null;
    if (board && (nextId === currentOverride || (nextId == null && currentOverride == null))) return;
    setLinkingBoard(true);
    try {
      await run(() => setLeadBoardMutation.mutateAsync(nextId), {
        context: "updateBoard",
        successKey: "leadBoardLinkSaved",
      });
    } finally {
      setLinkingBoard(false);
    }
  };

  const handleOpenCard = (card) => {
    setSelectedCard(card);
    setCardModalOpen(true);
  };

  if (!leadId) return null;

  if (!active) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
        {t("sidebarTasksCollapsedHint")}
      </Typography>
    );
  }

  if (isLoading || leadTasksLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 2.5, gap: 1 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (isError || !board) {
    return (
      <Box>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
            {t("sidebarTasksLoadError")}
          </Typography>
        </Alert>
        {canChangeLeadBoard ? (
          <Autocomplete
            size="small"
            sx={{ mt: 1.5 }}
            options={boardOptions}
            value={selectedBoardOption}
            onChange={handleBoardLinkChange}
            disabled={linkingBoard || setLeadBoardMutation.isLoading}
            getOptionLabel={(opt) => opt?.name || ""}
            isOptionEqualToValue={(a, b) =>
              (a?.id == null && b?.id == null) || Number(a?.id) === Number(b?.id)
            }
            renderInput={(params) => (
              <TextField {...params} label={t("leadBoardLinkLabel")} placeholder={t("leadBoardLinkPlaceholder")} />
            )}
          />
        ) : null}
      </Box>
    );
  }

  return (
    <Box>
      {openCount > 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 600 }}>
          {t("sidebarTasksOpenSummary", { count: openCount })}
        </Typography>
      ) : null}

      <Box
        sx={{
          mb: 1.25,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          overflow: "hidden",
        }}
      >
        {taskRows.length === 0 ? (
          <Box sx={{ px: 1.5, py: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {t("sidebarTasksListEmpty")}
            </Typography>
          </Box>
        ) : (
          visibleTasks.map((task, index) => {
            const due = formatDueChip(task.dueAt);
            const isLast = index === visibleTasks.length - 1 && hiddenCount === 0;

            return (
              <Box
                key={task.id}
                onClick={() => handleOpenCard(task)}
                sx={{
                  px: 1.25,
                  py: 1,
                  cursor: "pointer",
                  borderBottom: isLast ? "none" : `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ minWidth: 0 }}>
                  {task.isRequired ? (
                    <FlagOutlinedIcon
                      sx={{ fontSize: 15, color: "warning.main", mt: 0.25, flexShrink: 0 }}
                      aria-hidden
                    />
                  ) : null}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        lineHeight: 1.4,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {formatTaskDisplayName(task.id, task.title)}
                    </Typography>
                    {task.boardName ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.2, lineHeight: 1.3 }}
                      >
                        {t("sidebarTaskBoardName", { name: task.boardName })}
                      </Typography>
                    ) : null}
                    {due ? (
                      <Stack direction="row" alignItems="center" spacing={0.35} sx={{ mt: 0.3 }}>
                        {task.columnIsDone ? (
                          <CheckCircleOutlineIcon sx={{ fontSize: 13, color: "success.main" }} />
                        ) : (
                          <ScheduleOutlinedIcon
                            sx={{
                              fontSize: 13,
                              color: due.overdue ? "error.main" : "text.secondary",
                            }}
                          />
                        )}
                        <Typography
                          variant="caption"
                          color={
                            task.columnIsDone
                              ? "success.main"
                              : due.overdue
                                ? "error.main"
                                : "text.secondary"
                          }
                          fontWeight={task.columnIsDone || due.overdue ? 700 : 500}
                        >
                          {due.label}
                        </Typography>
                      </Stack>
                    ) : null}
                  </Box>
                  <Chip
                    label={task.columnName}
                    size="small"
                    variant={task.columnIsDone ? "filled" : "outlined"}
                    color={task.columnIsDone ? "success" : "default"}
                    sx={{
                      height: 22,
                      maxWidth: 100,
                      flexShrink: 0,
                      fontWeight: 600,
                      fontSize: "0.68rem",
                      "& .MuiChip-label": {
                        px: 0.75,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                </Stack>
              </Box>
            );
          })
        )}

        {hiddenCount > 0 ? (
          <Box
            sx={{
              px: 1.25,
              py: 0.75,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
            }}
          >
            <Typography
              component="button"
              type="button"
              onClick={() => leadId && navigate(tasksPaths.lead(leadId))}
              sx={{
                border: "none",
                bgcolor: "transparent",
                color: "primary.main",
                fontSize: "0.8125rem",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                p: 0,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {t("sidebarTasksShowMore", { count: hiddenCount })}
            </Typography>
          </Box>
        ) : null}
      </Box>

      {canCreate && defaultColumn ? (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
          <TextField
            size="small"
            fullWidth
            placeholder={t("sidebarAddTaskPlaceholder")}
            value={newTaskTitle}
            onChange={(e) => setNewTaskTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddTask();
              }
            }}
            disabled={addingTask}
          />
          <Tooltip title={addingTask ? t("addingCard") : t("addCard")}>
            <span>
              <IconButton
                color="primary"
                onClick={handleAddTask}
                disabled={!newTaskTitle.trim() || addingTask}
                sx={{
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  borderRadius: 1.5,
                }}
              >
                {addingTask ? <CircularProgress size={18} /> : <AddIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ) : null}

      <Button
        size="small"
        onClick={() => setMoreOpen((v) => !v)}
        endIcon={
          <ExpandMoreIcon
            sx={{
              fontSize: 18,
              transform: moreOpen ? "rotate(180deg)" : "none",
              transition: "transform 0.15s ease",
            }}
          />
        }
        sx={{
          mt: 0.5,
          px: 0.5,
          minWidth: 0,
          color: "text.secondary",
          fontWeight: 600,
          textTransform: "none",
          "&:hover": { bgcolor: "transparent", color: "text.primary" },
        }}
      >
        {t("sidebarTasksMoreOptions")}
      </Button>

      <Collapse in={moreOpen} unmountOnExit>
        <Box
          sx={{
            mt: 0.75,
            p: 1.25,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            bgcolor: alpha(theme.palette.action.hover, 0.02),
          }}
        >
          <Stack direction="row" spacing={0.5} sx={{ mb: 1.25 }}>
            {canCreate && defaultColumn ? (
              <Tooltip title={t("aiTasksButtonHint")}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setAiTasksOpen(true)}
                    aria-label={t("aiTasksButtonLabel")}
                    sx={{
                      border: 1,
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderRadius: 1.5,
                      color: "primary.main",
                    }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            <Tooltip title={t("openTaskBoard")}>
              <span>
                <IconButton
                  size="small"
                  onClick={openLinkedBoard}
                  aria-label={t("openTaskBoard")}
                  sx={{ border: 1, borderColor: "divider", borderRadius: 1.5 }}
                >
                  <ViewKanbanOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            {!inCockpit ? (
              <OpenInPopupIconButton
                title={t("openInPopupHint")}
                ariaLabel={t("openInPopup")}
                disabled={!board?.id && !leadId}
                onClick={openLinkedBoardPopup}
              />
            ) : null}
          </Stack>

          <Divider sx={{ mb: 1.25 }} />

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, fontWeight: 600 }}>
            {t("leadBoardLinkSectionTitle")}
          </Typography>
          {boardSourceHint || board?.name ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, lineHeight: 1.4 }}>
              {boardSourceHint
                ? t("leadBoardCurrentHint", { board: board.name, source: boardSourceHint })
                : t("leadBoardCurrentDefaultHint", { board: board.name })}
            </Typography>
          ) : null}

          {canChangeLeadBoard ? (
            <Autocomplete
              size="small"
              options={boardOptions}
              value={selectedBoardOption}
              onChange={handleBoardLinkChange}
              disabled={linkingBoard || setLeadBoardMutation.isLoading}
              getOptionLabel={(opt) => opt?.name || ""}
              isOptionEqualToValue={(a, b) =>
                (a?.id == null && b?.id == null) || Number(a?.id) === Number(b?.id)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("leadBoardLinkLabel")}
                  placeholder={t("leadBoardLinkPlaceholder")}
                />
              )}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {board.name}
            </Typography>
          )}
        </Box>
      </Collapse>

      <TaskCardModalContainer
        open={cardModalOpen}
        card={selectedCard}
        board={
          selectedCard?.boardId && Number(selectedCard.boardId) !== Number(board?.id)
            ? null
            : board
        }
        boardId={selectedCard?.boardId || board?.id}
        onClose={() => {
          setCardModalOpen(false);
          setSelectedCard(null);
          refetchLeadTasks();
        }}
        extraCacheKeys={leadId ? [["teamBoardForLead", leadId]] : []}
        contextLeadId={leadId}
      />

      <AiGenerateTasksDialog
        open={aiTasksOpen}
        onClose={() => setAiTasksOpen(false)}
        conversationId={leadId}
        messages={messages}
        defaultColumnId={defaultColumn?.id}
        mutations={mutations}
        run={run}
      />
    </Box>
  );
};

const LeadTasksSidebarBoard = (props) => (
  <TeamTasksFeedbackProvider>
    <LeadTasksSidebarBoardInner {...props} />
  </TeamTasksFeedbackProvider>
);

export default LeadTasksSidebarBoard;
